import React from 'react';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import type { RegroundPayload, RegroundRadius } from './api';
import { RADIUS_ORDER, RADIUS_STYLES, asCount } from './constants';
import RegroundDecisionRow from './RegroundDecision';
import RegroundSentences from './RegroundSentences';

/**
 * The grounding audit: which sentences of a report trace back to something a consumer actually
 * said, and what the classifier would put in place of the ones that do not.
 *
 * Two rules the tests pin, because both are easy to render misleadingly:
 *
 *  - a `reverted` run changed NOTHING. The service attempted every block, scored its own rewrite
 *    no better, and rolled it back. Showing a gain there would tell an admin the report improved
 *    when it did not — so the gain is suppressed and the revert is stated.
 *  - the per-block proposals survive that revert, so they are shown either way. They are the
 *    reason this panel exists: "no consumer statement mentions BOGO or Costco" is a report
 *    asserting something nobody said.
 */

interface RegroundPanelProps {
  reground: RegroundPayload | null;
  running: boolean;
}

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="mx-6 mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
    {children}
  </section>
);

const Header: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <header className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-5 py-3.5">
    <h3 className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Grounding audit</h3>
    {children}
  </header>
);

/** The rungs, in order, from whichever shape the payload uses.
 *
 *  scale_version 2 keys the counts by tag name and states its own order in `tags`; v1 keys them by
 *  the numeric rung. Both are read, v2 first, because the tag-keyed form is v2's canonical one and
 *  the numeric key is compatibility that could go away — and the way this fails is silent, with the
 *  bar rendering nothing at all.
 *
 *  Labels always come from the payload's own vocabulary. v2 renamed the rungs AND changed what 1
 *  and 2 mean, so a stored v1 row read with v2 names would have every sentence relabelled. */
const rungsOf = (radius: RegroundRadius): Array<{ key: string; label: string; count: number }> => {
  const byTag = radius.distribution_by_tag;
  if (radius.tags?.length && byTag) {
    return radius.tags.map((tag, index) => ({
      key: String(index),
      label: tag,
      count: byTag[tag] ?? 0,
    }));
  }
  const distribution = radius.distribution ?? {};
  return RADIUS_ORDER.map((level) => ({
    key: level,
    label: radius.scale?.[level] ?? level,
    count: distribution[level] ?? 0,
  }));
};

const GroundingBar: React.FC<{ radius: RegroundRadius }> = ({ radius }) => {
  const rungs = rungsOf(radius);
  const total = rungs.reduce((sum, rung) => sum + rung.count, 0);
  if (total === 0) return null;

  return (
    <div>
      <div className="mb-2 flex h-2 overflow-hidden rounded-full bg-gray-100">
        {rungs.map((rung) =>
          rung.count === 0 ? null : (
            <div
              key={rung.key}
              className={RADIUS_STYLES[rung.key]}
              style={{ width: `${(rung.count / total) * 100}%` }}
            />
          ),
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {rungs.map((rung) =>
          rung.count === 0 ? null : (
            <span key={rung.key} className="flex items-center gap-1.5 text-[11.5px] text-gray-600">
              <span
                className={`h-2 w-2 rounded-full ${RADIUS_STYLES[rung.key]}`}
                aria-hidden="true"
              />
              {rung.label} · {rung.count}
            </span>
          ),
        )}
      </div>
    </div>
  );
};

const Note: React.FC<{ tone: 'amber' | 'red'; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <div
    className={`flex gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] ${
      tone === 'red'
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-amber-200 bg-amber-50 text-amber-800'
    }`}
  >
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </div>
);

const RegroundPanel: React.FC<RegroundPanelProps> = ({ reground, running }) => {
  if (running) {
    return (
      <Shell>
        <div className="flex items-center gap-2.5 px-5 py-4 text-[13px] text-gray-600">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" aria-hidden="true" />
          {/* No progress bar and no percentage: the service reports neither, and inventing one
              would be a guess dressed as a measurement. Measured runs span 4 to 12 minutes. */}
          <span>
            Grounding every sentence against what consumers actually said. This usually takes
            <strong className="font-semibold"> 5 to 10 minutes</strong> — the result appears here
            when it lands.
          </span>
        </div>
      </Shell>
    );
  }

  if (!reground) return null;

  if (reground.status === 'skipped') {
    return (
      <Shell>
        <Header />
        <div className="px-5 py-4">
          <div className="flex gap-2 text-[12.5px] text-gray-600">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
            <span>
              The classifier skipped this one:{' '}
              <span className="font-medium text-gray-800">{reground.reason}</span>
            </span>
          </div>
        </div>
      </Shell>
    );
  }

  if (reground.status === 'error') {
    return (
      <Shell>
        <Header />
        <div className="px-5 py-4">
          <Note tone="red">
            {/* Prose, not monospace: the service's failures are written as sentences — "…Re-run
                annotate_report before regrounding" — and a code block makes them read as opaque. */}
            <strong className="font-semibold">The audit failed.</strong>{' '}
            <span>{reground.error}</span>
          </Note>
        </div>
      </Shell>
    );
  }

  const audit = reground.reground ?? null;
  const radius = reground.radius ?? null;
  // v2 renamed `novel_count` to `directional_count`. Both are sent today, but a payload carrying
  // only the v2 name would drop this line entirely — and a missing sentence is invisible.
  const untraceable = radius?.novel_count ?? radius?.directional_count ?? null;
  const reverted = audit?.reverted === true;
  const before = audit?.rating_before?.expected_rating_1to5 ?? null;
  const after = audit?.rating_after?.expected_rating_1to5 ?? null;
  const gain = audit?.rating_gain ?? null;
  const complaints = audit?.rater_complaints_not_fixable_by_prose ?? [];
  const decisions = audit?.decisions ?? [];

  return (
    <Shell>
      <Header>
        {audit &&
          (reverted ? (
            <span
              className="rounded-lg border border-gray-200 bg-gray-100 px-2.5 py-1
                             text-[11.5px] font-bold uppercase tracking-wide text-gray-600"
            >
              nothing was changed
            </span>
          ) : (
            <span className="flex items-baseline gap-1.5 text-[13px] text-gray-500">
              <span className="font-semibold text-gray-900">{before?.toFixed(2) ?? '—'}</span>
              <span aria-hidden="true">→</span>
              <span className="font-semibold text-gray-900">{after?.toFixed(2) ?? '—'}</span>
              {gain != null && gain !== 0 && (
                <span
                  className="ml-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5
                                 text-[11.5px] font-bold text-green-700"
                >
                  +{gain.toFixed(2)}
                </span>
              )}
            </span>
          ))}
        {audit?.blocks_changed != null && audit.blocks_flagged != null && (
          <span className="ml-auto text-[12px] text-gray-500">
            {audit.blocks_changed} of {audit.blocks_flagged} blocks rewritten
          </span>
        )}
      </Header>

      <div className="space-y-4 px-5 py-4">
        {reverted && (
          <Note tone="amber">
            The classifier rewrote every flagged block, scored the result, and found it no better —
            so it <strong className="font-semibold">reverted the whole rewrite</strong>. The report
            is unchanged. What it would have written is below, and still worth reading: the reasons
            name what the prose could not support.
          </Note>
        )}

        {radius && (
          <div>
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                Where the sentences come from
              </h4>
              {radius.scale_version != null && (
                <span
                  className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5
                                 text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  scale v{radius.scale_version}
                </span>
              )}
              {untraceable != null && radius.sentences_scored != null && (
                <span className="text-[12.5px] text-gray-600">
                  <strong className="font-semibold text-gray-900">
                    {untraceable} of {radius.sentences_scored}
                  </strong>{' '}
                  trace back to nothing a consumer said
                </span>
              )}
            </div>
            <GroundingBar radius={radius} />
            <div className="mt-3">
              <RegroundSentences radius={radius} />
            </div>
            {radius.corpus_consumers != null && (
              <p className="mt-2 text-[11.5px] text-gray-500">
                Checked against {asCount(radius.corpus_size)} statements from{' '}
                {asCount(radius.corpus_consumers)} consumers
                {audit?.untraceable_before != null &&
                  audit?.untraceable_after != null &&
                  ` · untraceable sentences ${audit.untraceable_before} → ${audit.untraceable_after}`}
              </p>
            )}
          </div>
        )}

        {decisions.length > 0 && (
          <details open className="border-t border-gray-100 pt-3">
            <summary
              className="mb-2 cursor-pointer text-[11px] font-bold uppercase tracking-wide
                                text-gray-400"
            >
              What it would change ({decisions.length})
            </summary>
            <div className="space-y-2.5">
              {decisions.map((decision, index) => (
                <RegroundDecisionRow key={`${decision.path}-${index}`} decision={decision} />
              ))}
            </div>
          </details>
        )}

        {complaints.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
              What rewriting the prose cannot fix
            </h4>
            <ul className="space-y-1">
              {complaints.map((complaint) => (
                <li key={complaint} className="text-[12.5px] text-gray-600">
                  — {complaint}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Shell>
  );
};

export default RegroundPanel;
